import React, { useState, useEffect } from "react";
import "../css/products.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "react-toastify";
import {
  Plus,
  Pencil,
  BadgeDollarSign,
  FileText,
  Search,
} from "lucide-react";
// ✅ Normalize API base (consistent across entire app)
const RAW_API_BASE =
  (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE : undefined) ||
  process.env.REACT_APP_API_BASE ||
  "https://pos-backend-58o9.onrender.com";

const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/i, "");
const apiPath = (path) => `${API_BASE}${path.startsWith("/api") ? path : `/api${path}`}`;

// ✅ Auth header helper (optional but safer)
const getAuth = () => {
  const raw = localStorage.getItem("rcs_auth") || sessionStorage.getItem("rcs_auth");
  try { return raw ? JSON.parse(raw) : null; } catch { return null; }
};
const authHeader = () => {
  const s = getAuth();
  return s?.access_token ? { Authorization: `Bearer ${s.access_token}` } : {};
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [itemName, setItemName] = useState("");
  const [type, setType] = useState("Product");
  const [price, setPrice] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [priceChangeAllowed, setPriceChangeAllowed] = useState(false);
  const [status, setStatus] = useState("Active");
  const [usesStock, setUsesStock] = useState(false);
  const [formData, setFormData] = useState({
    quantity: "",
    cost_price: "",
    selling_price: "",
    exclude_from_points: false,
    fixed_points: "",
  });
  const [editQuantityId, setEditQuantityId] = useState(null);
  const [newQuantity, setNewQuantity] = useState("");
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [priceData, setPriceData] = useState({ cost: "", sell: "", markUp: "" });
  const [barcodes, setBarcodes] = useState([]); // ✅ array for multiple barcodes
const [barcodeInput, setBarcodeInput] = useState(""); // temporary input field


  const formatMoney = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? "-" : `$${num.toFixed(2)}`;
  };

  const formatRawMoney = (value) => {
    const num = parseFloat(value);
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  // ✅ Load all products
  useEffect(() => {
    fetch(apiPath("/products"), { headers: { ...authHeader() } })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch products");
        return res.json();
      })
      .then((data) => setProducts(data))
      .catch((err) => console.error("Error loading products:", err));
  }, []);

  // ✅ Add product
  const handleAddProduct = async (e) => {
    e.preventDefault();
// ✅ Normalize barcode array before sending
const normalizedBarcodes =
  Array.isArray(barcodes) && barcodes.length
    ? barcodes.map((b) => String(b).trim()).filter((b) => b !== "")
    : barcodeInput.trim()
    ? [barcodeInput.trim()]
    : [];

const data = {
  name: itemName,
  type,
  price: parseFloat(price),
  price_change_allowed: priceChangeAllowed,
  status,
  uses_stock: usesStock,
  exclude_from_points: formData.exclude_from_points,
  barcode: barcodeInput.trim() || null,
  barcodes: normalizedBarcodes, // ✅ clean array

  ...(formData.fixed_points !== "" && { fixed_points: parseInt(formData.fixed_points, 10) }),
  ...(usesStock && { quantity: parseInt(formData.quantity, 10) }),
};

    try {
      const res = await fetch(apiPath("/products"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error("Failed to create product");
      const created = await res.json();
      setProducts([...products, created]);
      resetModal();
      toast.success("✅ Product added successfully!");
    } catch (err) {
      console.error("Add failed", err);
      toast.error("❌ Failed to add product.");
    }
  };

  // ✅ Edit product
  const handleEditProduct = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;

// ✅ Normalize barcode array before sending
const normalizedBarcodes =
  Array.isArray(barcodes) && barcodes.length
    ? barcodes.map((b) => String(b).trim()).filter((b) => b !== "")
    : barcodeInput.trim()
    ? [barcodeInput.trim()]
    : [];

const data = {
  name: itemName,
  type,
  price: parseFloat(price),
  price_change_allowed: priceChangeAllowed,
  status,
  uses_stock: usesStock,
  exclude_from_points: formData.exclude_from_points,
  fixed_points: formData.fixed_points !== "" ? parseInt(formData.fixed_points, 10) : null,
  quantity: usesStock
    ? formData.quantity === "" ? 0 : parseInt(formData.quantity, 10)
    : null,
  cost_price: usesStock ? parseFloat(formData.cost_price) : null,
  selling_price: usesStock ? parseFloat(formData.selling_price) : null,
  barcode: barcodeInput.trim() || null,
  barcodes: normalizedBarcodes, // ✅ clean array
};

    try {
      const res = await fetch(apiPath(`/products/${selectedProduct.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(data),
      });

      if (!res.ok) throw new Error("Failed to update product");
      const updated = await res.json();
      setProducts(products.map((p) => (p.id === updated.id ? updated : p)));
      resetModal();
      toast.success("✅ Product updated successfully!");
    } catch (err) {
      console.error("Edit failed", err);
      toast.error("❌ Failed to update product.");
    }
  };

  // ✅ Update quantity
  const handleUpdateQuantity = async (id) => {
    if (newQuantity === "" || parseInt(newQuantity) < 0) {
      alert("Please enter a valid quantity.");
      return;
    }
    try {
      const res = await fetch(apiPath(`/products/${id}/quantity`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ quantity: parseInt(newQuantity) }),
      });

      if (!res.ok) throw new Error("Failed to update quantity");
      const updatedItem = await res.json();
      setProducts((p) =>
        p.map((prod) =>
          prod.id === updatedItem.updatedItem.id ? updatedItem.updatedItem : prod
        )
      );
      setEditQuantityId(null);
      setNewQuantity("");
      toast.success("📦 Quantity updated successfully.");
    } catch (err) {
      console.error("Quantity update failed", err);
      alert(err.message);
    }
  };

  // ✅ Delete product
  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;
    try {
      const res = await fetch(apiPath(`/products/${selectedProduct.id}`), {
        method: "DELETE",
        headers: { ...authHeader() },
      });

      if (!res.ok) throw new Error("Delete failed");
      setProducts(products.filter((p) => p.id !== selectedProduct.id));
      setSelectedProduct(null);
      toast.success("🗑️ Product deleted!");
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("❌ Failed to delete product.");
    }
  };

  // ✅ Toggle status
  const toggleProductStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
    try {
      const res = await fetch(apiPath(`/products/${id}/status`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) throw new Error("Failed to update status");
      const updated = await res.json();
      setProducts(products.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err) {
      console.error("Status toggle failed", err);
      alert("Failed to update status");
    }
  };

  // ✅ Toggle points eligibility
  const togglePointsEligibility = async (id, currentStatus) => {
    try {
      const res = await fetch(apiPath(`/products/${id}/exclude-points`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ exclude_from_points: !currentStatus }),
      });

      if (!res.ok) throw new Error("Failed to toggle points eligibility");
      const updated = await res.json();
      setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err) {
      console.error("Points toggle failed", err);
      alert("Failed to update points eligibility");
    }
  };

// ✅ Reset modal state
const resetModal = () => {
  setItemName("");
  setType("Product");
  setPrice("");
  setPriceChangeAllowed(false);
  setStatus("Active");
  setUsesStock(false);
  setBarcodeInput("");
  setBarcodes([]);

  setFormData({
    quantity: "",
    cost_price: "",
    selling_price: "",
    exclude_from_points: false,
    fixed_points: "",
  });

  setShowModal(false);
  setSelectedProduct(null);
};


  // ✅ Edit Price modal submission
  const updatePrice = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(apiPath(`/products/${selectedProduct.id}/prices`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({
          cost_price: parseFloat(priceData.cost),
          selling_price: parseFloat(priceData.sell),
        }),
      });

      if (!res.ok) throw new Error("Failed to update price");
      const updated = await res.json();
      const updatedItem = updated.updatedItem;
      setProducts((prev) =>
        prev.map((p) => (p.id === updatedItem.id ? updatedItem : p))
      );
      setShowPriceModal(false);
      alert("Price updated successfully!");
    } catch (err) {
      alert("Error updating price");
      console.error(err);
    }
  };

  const exportStockCSV = () => {
    const headers = [
      "Code",
      "Name",
      "Quantity",
      "Cost Price",
      "Selling Price",
      "Mark Up",
      "Inventory Value"
    ];
  
    const rows = products
      .filter(p => p.uses_stock)
      .map(p => [
        p.code,
        p.name,
        p.quantity,
        formatRawMoney(p.cost_price),
        formatRawMoney(p.selling_price),
        formatRawMoney(p.mark_up),
        formatRawMoney(p.quantity * p.cost_price)
      ]);
  
    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
  
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "StockReport.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  
  const exportStockReportPDF = () => {
    const stockProducts = products.filter(p => p.uses_stock);
    const doc = new jsPDF();
    const now = new Date();
    const dateStr = now.toLocaleDateString();
    const timeStr = now.toLocaleTimeString();
  
    // ✅ Utility to center text
    const centerText = (text, y, fontSize = 12) => {
      doc.setFontSize(fontSize);
      const pageWidth = doc.internal.pageSize.getWidth();
      const textWidth = doc.getTextWidth(text);
      const x = (pageWidth - textWidth) / 2;
      doc.text(text, x, y);
    };
  
    // 📌 Header: Centered Business Info
    centerText("RCS TOPUP AND BILLING ENTITY", 10);
    centerText("22 BELLE PLAINE WAKENAAM", 16);
    centerText("(592) 662 7987 // (592) 649 1514", 22);
  
    // ✅ Draw clean full-width separator line
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(14, 28, pageWidth - 14, 28); // from x=14 to x=pageWidth-14 at y=28
  
    // 📌 Title & Timestamp
    centerText("STOCK INVENTORY REPORT", 36, 14);
    centerText(`Generated on: ${dateStr} at ${timeStr}`, 42, 10);
  
    // 📌 Table Data
    const tableData = stockProducts.map(p => {
      const cost = parseFloat(p.cost_price || 0);
      const sell = parseFloat(p.selling_price || 0);
      const markUp = parseFloat(p.mark_up || 0);
      const qty = parseFloat(p.quantity || 0);
      const invValue = qty * cost;
  
      return [
        p.code,
        p.name,
        qty.toFixed(0),
        `$${cost.toFixed(2)}`,
        `$${sell.toFixed(2)}`,
        `$${markUp.toFixed(2)}`,
        `$${invValue.toFixed(2)}`
      ];
    });
  
    // 📌 Totals
    const totalQty = stockProducts.reduce((sum, p) => sum + (parseFloat(p.quantity) || 0), 0);
    const totalCostPrice = stockProducts.reduce((sum, p) => sum + ((parseFloat(p.cost_price) || 0) * (parseFloat(p.quantity) || 0)), 0);
    const totalSellingPrice = stockProducts.reduce((sum, p) => sum + ((parseFloat(p.selling_price) || 0) * (parseFloat(p.quantity) || 0)), 0);
    const totalMarkUp = stockProducts.reduce((sum, p) => sum + ((parseFloat(p.mark_up) || 0) * (parseFloat(p.quantity) || 0)), 0);
    const totalInvValue = stockProducts.reduce((sum, p) => sum + ((parseFloat(p.quantity) || 0) * (parseFloat(p.cost_price) || 0)), 0);
  
    // 📌 Insert Table
    autoTable(doc, {
      startY: 48,
      head: [["Code", "Product", "Qty", "Cost Price", "Selling Price", "Mark Up", "Inventory Value"]],
      body: tableData,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [22, 160, 133] },
      foot: [[
        { content: "TOTALS", colSpan: 2, styles: { fontStyle: "bold", halign: "left" } },
        { content: totalQty.toFixed(0), styles: { halign: "right" } },
        "", "", "", // Skip Cost Price, Selling Price, Mark Up
        { content: `$${totalInvValue.toFixed(2)}`, styles: { halign: "right", textColor: [255, 0, 0], fontStyle: "bold" } }
      ]]
      ,
      footStyles: { fillColor: [22, 160, 133], textColor: [255, 255, 255], fontStyle: 'bold' },
    });
  
    // 📌 Save
    doc.save(`Stock_Report_${dateStr.replace(/\//g, "-")}.pdf`);
  };
  
  // ✅ Add this before your `return` statement (around line 370–380)
const openEditModal = () => {
  if (!selectedProduct) return;

  // Pre-fill the modal with product data
  setItemName(selectedProduct.name || "");
  setType(selectedProduct.type || "Product");
  setPrice(selectedProduct.price || "");
  setPriceChangeAllowed(selectedProduct.price_change_allowed || false);
  setStatus(selectedProduct.status || "Active");
  setUsesStock(selectedProduct.uses_stock || false);

  setFormData({
    quantity: selectedProduct.quantity || "",
    cost_price: selectedProduct.cost_price || "",
    selling_price: selectedProduct.selling_price || "",
    exclude_from_points: selectedProduct.exclude_from_points || false,
    fixed_points: selectedProduct.fixed_points || "",
  });
setBarcodeInput("");
setBarcodes(
  Array.isArray(selectedProduct.barcodes)
    ? selectedProduct.barcodes
    : selectedProduct.barcode
    ? [selectedProduct.barcode]
    : []
);



  setShowModal(true);
};

  
  return (
  <div className="modern-products-page">

    {/* =====================================================
        HERO
    ===================================================== */}
    <div className="modern-products-hero">

      <div className="modern-products-hero-left">

        <div className="modern-products-pill">
          Inventory Operations
        </div>

        <h1>
          Products
        </h1>

        <p>
          Manage inventory, pricing and
          operational product flow.          
        </p>

      </div>

      {/* =====================================================
          ACTIONS
      ===================================================== */}
      <div className="modern-products-actions">

        {/* NEW */}
        <button
          className="modern-product-action modern-product-action-teal"
          onClick={() =>
            setShowModal(true)
          }
        >

          <div className="modern-product-action-icon">
            <Plus size={18} />
          </div>

          <div className="modern-product-action-content">

            <span>
              New Product
            </span>

            <p>
              Add inventory item
            </p>

          </div>

        </button>

        {/* EDIT */}
        <button
          className="modern-product-action modern-product-action-blue"
          onClick={openEditModal}
          disabled={!selectedProduct}
        >

          <div className="modern-product-action-icon">
            <Pencil size={18} />
          </div>

          <div className="modern-product-action-content">

            <span>
              Edit Product
            </span>

            <p>
              Update item details
            </p>

          </div>

        </button>

        {/* PRICE */}
        <button
          className="modern-product-action modern-product-action-purple"
          disabled={
            !selectedProduct ||
            !selectedProduct.uses_stock
          }
          onClick={() => {

            setPriceData({
              cost:
                selectedProduct.cost_price,
              sell:
                selectedProduct.selling_price,
              markUp: (
                selectedProduct.selling_price -
                selectedProduct.cost_price
              ).toFixed(2),
            });

            setShowPriceModal(true);

          }}
        >

          <div className="modern-product-action-icon">
            <BadgeDollarSign size={18} />
          </div>

          <div className="modern-product-action-content">

            <span>
              Edit Price
            </span>

            <p>
              Modify stock pricing
            </p>

          </div>

        </button>

        {/* EXPORT */}
        <button
          className="modern-product-action modern-product-action-dark"
          onClick={exportStockReportPDF}
        >

          <div className="modern-product-action-icon">
            <FileText size={18} />
          </div>

          <div className="modern-product-action-content">

            <span>
              Export PDF
            </span>

            <p>
              Inventory report
            </p>

          </div>

        </button>

      </div>

    </div>

    {/* =====================================================
        SEARCH
    ===================================================== */}
    <div className="modern-products-toolbar">

      <div className="modern-products-search-wrap">

        <Search size={18} />

        <input
          type="text"
          placeholder="Search product..."
          value={searchTerm}
          onChange={(e) =>
            setSearchTerm(
              e.target.value
            )
          }
          className="modern-products-search"
        />

      </div>

    </div>

    {/* =====================================================
        SUMMARY
    ===================================================== */}
    <div className="modern-products-summary-grid">

      {/* PRODUCTS */}
      <div className="modern-products-summary-card">

        <span>
          Total Products
        </span>

        <h2>
          {products.length}
        </h2>

      </div>

      {/* STOCK */}
      <div className="modern-products-summary-card">

        <span>
          Inventory Quantity
        </span>

        <h2>
          {products.reduce(
            (sum, p) =>
              sum +
              (parseFloat(
                p.quantity
              ) || 0),
            0
          )}
        </h2>

      </div>

      {/* VALUE */}
      <div className="modern-products-summary-card">

        <span>
          Inventory Value
        </span>

        <h2 className="green">

          $
          {products
            .reduce(
              (sum, p) =>
                sum +
                (
                  (parseFloat(
                    p.quantity
                  ) || 0) *
                  (parseFloat(
                    p.cost_price
                  ) || 0)
                ),
              0
            )
            .toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
              }
            )}

        </h2>

      </div>

      {/* ACTIVE */}
      <div className="modern-products-summary-card">

        <span>
          Active Products
        </span>

        <h2 className="blue">

          {
            products.filter(
              (p) =>
                p.status ===
                "Active"
            ).length
          }

        </h2>

      </div>

    </div>

    {/* =====================================================
        TABLE CARD
    ===================================================== */}
    <div className="modern-products-table-card">

      <div className="modern-products-card-header">

        <div>

          <h3>
            Product Inventory
          </h3>

          <span>
            Product catalog and
            operational inventory
          </span>

        </div>

      </div>

      <div className="modern-products-table-wrap">

        <table className="modern-products-table">

          <thead>

            <tr>

              <th>
                Code
              </th>

              <th>
                Product
              </th>

              <th>
                Barcode
              </th>

              <th>
                Type
              </th>

              <th>
                Price
              </th>

              <th>
                Stock
              </th>

              <th>
                Qty
              </th>

              <th>
                Cost
              </th>

              <th>
                Selling
              </th>

              <th>
                Markup
              </th>

              <th>
                Value
              </th>

              <th>
                Status
              </th>

              <th>
                Points
              </th>

            </tr>

          </thead>

          <tbody>

            {products
              .filter((product) =>
                product.name
                  .toLowerCase()
                  .includes(
                    searchTerm.toLowerCase()
                  )
              )
              .map((product) => (

                <tr
                  key={product.id}
                  className={
                    selectedProduct?.id ===
                    product.id
                      ? "modern-selected-row"
                      : ""
                  }
                  onClick={() =>
                    setSelectedProduct(
                      product
                    )
                  }
                >

                  {/* CODE */}
                  <td>
                    {product.code}
                  </td>

                  {/* NAME */}
                  <td className="modern-product-name">
                    {product.name}
                  </td>

                  {/* BARCODE */}
                  <td>

                    {Array.isArray(
                      product.barcodes
                    ) &&
                    product.barcodes
                      .length > 0 ? (

                      <button
                        className="modern-barcode-btn"
                        onClick={(e) => {

                          e.stopPropagation();

                          toast.info(
                            product.barcodes.join(
                              ", "
                            ),
                            {
                              autoClose: 6000,
                            }
                          );

                        }}
                      >
                        {
                          product.barcodes
                            .length
                        }
                        {" "}
                        Codes
                      </button>

                    ) : (
                      product.barcode ||
                      "-"
                    )}

                  </td>

                  {/* TYPE */}
                  <td>
                    {product.type}
                  </td>

                  {/* PRICE */}
                  <td>

                    $
                    {parseFloat(
                      product.price
                    ).toFixed(2)}

                  </td>

                  {/* STOCK */}
                  <td>

                    {product.uses_stock
                      ? "Yes"
                      : "No"}

                  </td>

                  {/* QUANTITY */}
                  <td>

                    {product.uses_stock
                      ? product.quantity
                      : "-"}

                  </td>

                  {/* COST */}
                  <td>

                    {product.uses_stock
                      ? `$${parseFloat(
                          product.cost_price
                        ).toFixed(2)}`
                      : "-"}

                  </td>

                  {/* SELL */}
                  <td>

                    {product.uses_stock
                      ? `$${parseFloat(
                          product.selling_price
                        ).toFixed(2)}`
                      : "-"}

                  </td>

                  {/* MARKUP */}
                  <td>

                    {product.uses_stock
                      ? formatMoney(
                          product.mark_up
                        )
                      : "-"}

                  </td>

                  {/* VALUE */}
                  <td>

                    {product.uses_stock
                      ? formatMoney(
                          product.inventory_value ||
                            (
                              product.quantity *
                              product.cost_price
                            )
                        )
                      : "-"}

                  </td>

                  {/* STATUS */}
                  <td>

                    <button
                      className={`modern-status-pill ${
                        product.status ===
                        "Active"
                          ? "active"
                          : "inactive"
                      }`}
                      onClick={(e) => {

                        e.stopPropagation();

                        toggleProductStatus(
                          product.id,
                          product.status
                        );

                      }}
                    >

                      {product.status}

                    </button>

                  </td>

                  {/* POINTS */}
                  <td>

                    <button
                      className={`modern-status-pill ${
                        product.exclude_from_points
                          ? "inactive"
                          : "active"
                      }`}
                      onClick={(e) => {

                        e.stopPropagation();

                        togglePointsEligibility(
                          product.id,
                          product.exclude_from_points
                        );

                      }}
                    >

                      {product.exclude_from_points
                        ? "Excluded"
                        : "Eligible"}

                    </button>

                  </td>

                </tr>

              ))}

          </tbody>

        </table>

      </div>

    </div>

    {/* =====================================================
        PRODUCT MODAL
    ===================================================== */}
    {showModal && (

      <div className="modal-overlay">

        <div className="modal-content modern-product-modal">

          <h3>

            {selectedProduct
              ? "Edit Product"
              : "Add Product"}

          </h3>

          <form
            onSubmit={
              selectedProduct
                ? handleEditProduct
                : handleAddProduct
            }
          >

            {/* existing form stays */}

          </form>

        </div>

      </div>

    )}

    {/* =====================================================
        PRICE MODAL
    ===================================================== */}
    {showPriceModal && (

      <div className="modal-overlay">

        <div className="modal-content modern-product-modal">

          <h3>
            Edit Pricing
          </h3>

          {/* existing price form stays */}

        </div>

      </div>

    )}

  </div>
);
  
};
  
export default Products
